import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useMaturityAssessment, useMaturityFramework, useService } from '../../graphql/queries';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import { canCreateAssessment } from '../../lib/permissions';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { MaturitySpiderChart, MaturityScoreBar } from '../../components/maturity';
import { deleteMaturityAssessment, approveMaturityAssessment, ApiError } from '../../lib/apiClient';
import { LIST_MATURITY_ASSESSMENTS, GET_MATURITY_ASSESSMENT } from '../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './$serviceId.assessments.$assessmentId.css';

export const Route = createFileRoute('/services/$serviceId/assessments/$assessmentId')({
  component: AssessmentDetailPage,
});

export { AssessmentDetailPage };

function AssessmentDetailPage(): JSX.Element {
  const { serviceId, assessmentId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const currentUser = useAppSelector(selectUser);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedCategoryId, setHighlightedCategoryId] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useMaturityAssessment(assessmentId);
  const assessment = data?.maturityAssessment;

  const { data: fwData, loading: fwLoading } = useMaturityFramework(assessment?.frameworkId ?? '');
  const { data: serviceData } = useService(serviceId);
  const framework = fwData?.maturityFramework;

  const CardElement = 'forge-card' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (loading || fwLoading) {
    return (
      <div className="assessment-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  if (fetchError || !assessment) {
    return (
      <div className="assessment-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load assessment"
          description={fetchError?.message ?? 'Assessment not found'}
          actionLabel="Back to Assessments"
          onAction={() =>
            void navigate({ to: '/services/$serviceId/assessments', params: { serviceId } })
          }
        />
      </div>
    );
  }

  const categories = framework
    ? framework.areas.flatMap((a) => a.categories.map((c) => ({ id: c.id, name: c.name })))
    : [];

  const categoryScores = assessment.categoryScores ?? [];

  const areaGroups = framework
    ? framework.areas.map((area) => {
        const areaScore = assessment.areaScores?.find((s) => s.areaId === area.id);
        const cats = area.categories.map((cat) => {
          const catScore = categoryScores.find((s) => s.categoryId === cat.id);
          return { id: cat.id, name: cat.name, score: catScore?.score ?? 0 };
        });
        return { id: area.id, name: area.name, score: areaScore?.score ?? 0, categories: cats };
      })
    : [];

  const handleApprove = async (): Promise<void> => {
    setIsApproving(true);
    setError(null);
    try {
      await approveMaturityAssessment(assessmentId);
      await apolloClient.refetchQueries({
        include: [GET_MATURITY_ASSESSMENT, LIST_MATURITY_ASSESSMENTS],
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to approve assessment');
    } finally {
      setIsApproving(false);
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteMaturityAssessment(assessmentId);
      await apolloClient.refetchQueries({ include: [LIST_MATURITY_ASSESSMENTS] });
      void navigate({ to: '/services/$serviceId/assessments', params: { serviceId } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete assessment');
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const toggleCategory = (categoryId: string): void => {
    setHighlightedCategoryId((prev) => (prev === categoryId ? null : categoryId));
  };

  return (
    <div className="assessment-detail-page">
      {error && (
        <BannerElement theme="danger">
          <span>{error}</span>
        </BannerElement>
      )}

      <div className="page-header page-header--toolbar">
        <div className="page-header-left">
          <Link to="/services/$serviceId/assessments" params={{ serviceId }} className="back-link">
            ← Assessments
          </Link>
          <h1 className="forge-typography--headline5">
            {assessment.assessmentPeriod} Maturity Assessment
          </h1>
          <span
            className={`assessment-status-badge assessment-status-badge--${assessment.status.toLowerCase().replace('inprogress', 'in-progress')}`}
          >
            {assessment.status}
          </span>
        </div>

        <div className="page-header-actions">
          {assessment.status !== 'Approved' && (
            <PermissionGate permission="maturityAssessment:approve">
              <ForgeButton
                type="button"
                variant="raised"
                onClick={() => void handleApprove()}
                disabled={isApproving}
              >
                {isApproving ? 'Approving…' : 'Approve'}
              </ForgeButton>
            </PermissionGate>
          )}
          {canCreateAssessment(currentUser, serviceData?.service) && (
            <ForgeButton type="button" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </ForgeButton>
          )}
        </div>
      </div>

      <CardElement className="assessment-overview">
        <h2 className="forge-typography--subtitle1">Overview</h2>
        <dl className="detail-grid">
          <dt>Period</dt>
          <dd>{assessment.assessmentPeriod}</dd>
          <dt>Status</dt>
          <dd>
            <span
              className={`assessment-status-badge assessment-status-badge--${assessment.status.toLowerCase().replace('inprogress', 'in-progress')}`}
            >
              {assessment.status}
            </span>
          </dd>
          <dt>Overall Score</dt>
          <dd className="overall-score">
            {assessment.overallScore != null
              ? `${assessment.overallScore.toFixed(2)} / 6`
              : 'Not scored'}
          </dd>
          <dt>Completion</dt>
          <dd>{assessment.completionPercentage}%</dd>
          <dt>Due Date</dt>
          <dd>{new Date(assessment.dueDate).toLocaleDateString()}</dd>
          {assessment.assessmentDate && (
            <>
              <dt>Completed</dt>
              <dd>{new Date(assessment.assessmentDate).toLocaleDateString()}</dd>
            </>
          )}
          {assessment.approvedBy && (
            <>
              <dt>Approved By</dt>
              <dd>{assessment.approvedBy}</dd>
            </>
          )}
        </dl>
        {assessment.description && (
          <p className="forge-typography--body2 assessment-description">{assessment.description}</p>
        )}
      </CardElement>

      {categories.length > 0 && (
        <CardElement className="assessment-radar-card">
          <h2 className="forge-typography--subtitle1">Maturity Profile</h2>
          <div className="radar-layout">
            <MaturitySpiderChart
              categoryScores={categoryScores}
              categories={categories}
              highlightedCategoryId={highlightedCategoryId}
              size={380}
            />

            <div className="category-filter-panel">
              <p className="forge-typography--caption category-filter-label">
                Click a category to highlight it on the chart
              </p>
              <div className="category-filter-buttons">
                {categories.map((cat) => {
                  const score = categoryScores.find((s) => s.categoryId === cat.id);
                  return (
                    <button
                      key={cat.id}
                      className={`category-filter-btn${highlightedCategoryId === cat.id ? ' category-filter-btn--active' : ''}`}
                      onClick={() => toggleCategory(cat.id)}
                      aria-pressed={highlightedCategoryId === cat.id}
                    >
                      <span className="category-filter-btn__name">{cat.name}</span>
                      {score && (
                        <span className="category-filter-btn__score">{score.score.toFixed(1)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardElement>
      )}

      {areaGroups.map((area) => (
        <CardElement key={area.id} className="assessment-area-card">
          <div className="area-header">
            <h2 className="forge-typography--subtitle1">{area.name}</h2>
            {area.score > 0 && (
              <span className="area-score forge-typography--caption">
                Area avg: {area.score.toFixed(2)}
              </span>
            )}
          </div>
          <div className="area-categories">
            {area.categories.map((cat) => (
              <div key={cat.id} className="area-category">
                <MaturityScoreBar capabilityName={cat.name} score={cat.score} />
              </div>
            ))}
          </div>
        </CardElement>
      ))}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Assessment"
        message={`Are you sure you want to delete this assessment? This action cannot be undone.`}
        itemName={`${assessment.assessmentPeriod} Assessment`}
        isDeleting={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
}
